// Dashboard.tsx
// Home screen for logged-in users.
// Shows usage stats, time saved, and full evaluation history.
// Entry point to starting a new evaluation.

import React, { useState } from 'react';
import type { Theme, TierKey, EvaluationResult } from '../types';
import type { HistoryEntry } from '../hooks/useHistory';
import { TIERS } from '../lib/constants';
import { VERDICT_LABELS, TIER_LABELS } from '../utils/formatters';
import ThemeToggle from './ThemeToggle';
import styles from '../styles/Dashboard.module.css';

interface DashboardProps {
  history: HistoryEntry[];
  historyLoading: boolean;
  totalTimeSavedMinutes: number;
  tier: TierKey;
  used: number;
  onNewEvaluation: () => void;
  onZoteroImport: () => void;
  onSelectResult: (result: EvaluationResult) => void;
  onDeleteEvaluation: (id: string) => void;
  onUpgradeClick: () => void;
  theme: Theme;
  onToggleTheme: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatMinutes(minutes: number): string {
  if (minutes === 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const VERDICT_COLORS: Record<string, { color: string; bg: string }> = {
  must_read: { color: 'var(--verdict-must)', bg: 'var(--verdict-must-bg)' },
  worth_it:  { color: 'var(--verdict-worth)', bg: 'var(--verdict-worth-bg)' },
  skim:      { color: 'var(--verdict-skim)', bg: 'var(--verdict-skim-bg)' },
  skip:      { color: 'var(--verdict-skip)', bg: 'var(--verdict-skip-bg)' },
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function Dashboard({
  history,
  historyLoading,
  totalTimeSavedMinutes,
  tier,
  used,
  onNewEvaluation,
  onZoteroImport,
  onSelectResult,
  onDeleteEvaluation,
  onUpgradeClick,
  theme,
  onToggleTheme,
}: DashboardProps) {
  const [search, setSearch] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const limit = TIERS[tier].monthlyLimit;
  const remaining = limit === Infinity ? Infinity : Math.max(0, limit - used);
  const usagePct = limit === Infinity ? 0 : Math.min(100, (used / (limit as number)) * 100);
  const nearLimit = limit !== Infinity && usagePct >= 80;

  const filtered = history.filter(entry => {
    const matchSearch = entry.filename.toLowerCase().includes(search.toLowerCase());
    const matchVerdict = verdictFilter === 'all' || entry.verdict === verdictFilter;
    return matchSearch && matchVerdict;
  });

  const handleDelete = async (id: string) => {
    if (confirmDelete === id) {
      await onDeleteEvaluation(id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  return (
    <div className={styles.page}>

      {/* ── Top bar ── */}
      <div className={styles.topBar}>
        <h1 className={styles.topBarTitle}>Desk Reject</h1>
        <div className={styles.topBarActions}>
          <span className={styles.tierLabel}>{TIER_LABELS[tier]}</span>
          <button className={styles.btnUpgrade} onClick={onUpgradeClick}>
            {tier === 'lab' ? 'Manage plan' : 'Upgrade'}
          </button>
        </div>
      </div>

      <div className={styles.container}>

        {/* ── Stat cards ── */}
        <div className={styles.statsGrid}>

          {/* Papers evaluated */}
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Papers evaluated</div>
            <div className={styles.statValue}>{history.length}</div>
            <div className={styles.statSub}>all time</div>
          </div>

          {/* Time saved */}
          <div className={`${styles.statCard} ${styles.statCardAccent}`}>
            <div className={`${styles.statLabel} ${styles.statLabelAccent}`}>
              ⏱ Time saved
            </div>
            <div className={styles.statValue}>{formatMinutes(totalTimeSavedMinutes)}</div>
            <div className={styles.statSub}>vs reading everything</div>
          </div>

          {/* Usage this month */}
          <div className={`${styles.statCard} ${nearLimit ? styles.statCardWarning : ''}`}>
            <div className={`${styles.statLabel} ${nearLimit ? styles.statLabelWarning : ''}`}>
              This month
            </div>
            <div className={styles.statValue}>
              {used}
              {limit !== Infinity && (
                <span className={styles.statValueSuffix}>/{limit}</span>
              )}
            </div>
            {limit !== Infinity && (
              <div className={styles.usageBar}>
                <div
                  className={`${styles.usageBarFill} ${nearLimit ? styles.usageBarFillWarning : ''}`}
                  style={{ width: `${usagePct}%` }}
                />
              </div>
            )}
            <div className={`${styles.statSub} ${nearLimit ? styles.statSubWarning : ''}`}>
              {limit === Infinity ? 'Unlimited' : nearLimit ? (
                <span>
                  {remaining} remaining —{' '}
                  <button className={styles.upgradeLink} onClick={onUpgradeClick}>
                    upgrade
                  </button>
                </span>
              ) : `${remaining} remaining`}
            </div>
          </div>
        </div>

        {/* ── CTAs ── */}
        <button className={styles.btnEvaluate} onClick={onNewEvaluation}>
          + Evaluate new paper(s)
        </button>

        {(tier === 'dissertation' || tier === 'lab') && (
          <button className={styles.btnZotero} onClick={onZoteroImport}>
            📦 Import from Zotero
          </button>
        )}

        {/* ── History ── */}
        <div>
          <div className={styles.historyHeader}>
            <h2 className={styles.historyTitle}>Evaluation history</h2>
            <div className={styles.filters}>
              <input
                type="text"
                placeholder="Search by filename…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={styles.searchInput}
              />
              <select
                value={verdictFilter}
                onChange={e => setVerdictFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All verdicts</option>
                <option value="must_read">Must Read</option>
                <option value="worth_it">Worth It</option>
                <option value="skim">Skim</option>
                <option value="skip">Desk Reject</option>
              </select>
            </div>
          </div>

          {historyLoading ? (
            <div className={styles.loadingState}>Loading history…</div>
          ) : filtered.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📄</div>
              <div className={styles.emptyTitle}>
                {history.length === 0 ? 'No evaluations yet' : 'No results match your filters'}
              </div>
              <div className={styles.emptySubtitle}>
                {history.length === 0
                  ? 'Evaluate your first paper to get started'
                  : 'Try clearing the search or changing the filter'}
              </div>
            </div>
          ) : (
            <div className={styles.historyList}>
              {filtered.map((entry, i) => {
                const vc = VERDICT_COLORS[entry.verdict];
                const scoreClass = entry.overall_score >= 4
                  ? styles.scoreHigh
                  : entry.overall_score >= 3
                  ? styles.scoreMid
                  : styles.scoreLow;

                return (
                  <div
                    key={entry.id}
                    className={styles.historyRow}
                    onClick={() => onSelectResult(entry.result_json)}
                  >
                    <span
                      className={styles.verdictBadge}
                      style={vc ? { color: vc.color, background: vc.bg } : undefined}
                    >
                      {VERDICT_LABELS[entry.verdict as keyof typeof VERDICT_LABELS] || entry.verdict}
                    </span>

                    <div className={styles.filename}>
                      {entry.filename.replace('.pdf', '')}
                    </div>

                    <span className={`${styles.score} ${scoreClass}`}>
                      {Number(entry.overall_score).toFixed(1)}
                    </span>

                    <span className={styles.date}>{formatDate(entry.created_at)}</span>

                    <button
                      className={`${styles.btnDelete} ${confirmDelete === entry.id ? styles.btnDeleteConfirm : ''}`}
                      onClick={e => { e.stopPropagation(); handleDelete(entry.id); }}
                      title={confirmDelete === entry.id ? 'Click again to confirm' : 'Delete'}
                    >
                      {confirmDelete === entry.id ? 'confirm?' : '✕'}
                    </button>

                    <span className={styles.arrow}>→</span>
                  </div>
                );
              })}
            </div>
          )}

          {filtered.length > 0 && (
            <div className={styles.historyCount}>
              {filtered.length} evaluation{filtered.length !== 1 ? 's' : ''}
              {search || verdictFilter !== 'all' ? ' matching filters' : ''}
            </div>
          )}
        </div>
      </div>

      <ThemeToggle theme={theme} onToggle={onToggleTheme} />
    </div>
  );
}
